import React, { useState } from 'react'
import './MediaCarousel.css'

interface MediaItem {
  id: string
  type: 'image' | 'video'
  url: string
  description?: string
}

interface MediaCarouselProps {
  media: MediaItem[]
  className?: string
}

const MediaCarousel: React.FC<MediaCarouselProps> = ({ media, className = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  if (!media || media.length === 0) {
    return null
  }

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? media.length - 1 : prevIndex - 1
    )
  }

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === media.length - 1 ? 0 : prevIndex + 1
    )
  }

  const goToSlide = (index: number) => {
    setCurrentIndex(index)
  }

  const currentMedia = media[currentIndex]

  return (
    <div className={`media-carousel ${className}`}>
      <div className="media-carousel-container">
        <div className="media-carousel-track">
          <div className="media-carousel-slide">
            {currentMedia.type === 'image' ? (
              <img
                src={currentMedia.url}
                alt={currentMedia.description || `Media ${currentIndex + 1}`}
                className="media-carousel-image"
                loading="lazy"
              />
            ) : (
              <video
                src={currentMedia.url}
                className="media-carousel-video"
                controls
                preload="metadata"
              >
                Your browser does not support the video tag.
              </video>
            )}
            
            {currentMedia.description && (
              <div className="media-carousel-caption">
                {currentMedia.description}
              </div>
            )}
          </div>
        </div>

        {media.length > 1 && (
          <>
            <button
              className="media-carousel-nav prev"
              onClick={goToPrevious}
              aria-label="Previous media"
            >
              &#8249;
            </button>
            <button
              className="media-carousel-nav next"
              onClick={goToNext}
              aria-label="Next media"
            >
              &#8250;
            </button>
          </>
        )}
      </div>

      {media.length > 1 && (
        <div className="media-carousel-indicators">
          {media.map((_, index) => (
            <button
              key={index}
              className={`media-carousel-indicator ${
                index === currentIndex ? 'active' : ''
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to media ${index + 1}`}
            />
          ))}
        </div>
      )}

      {media.length > 1 && (
        <div className="media-carousel-counter">
          {currentIndex + 1} / {media.length}
        </div>
      )}
    </div>
  )
}

export default MediaCarousel
